import { useState, useEffect } from "react";
import { AdminLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { Ban, CheckCircle, Star, Eye, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRatingsDialog, setShowRatingsDialog] = useState(false);
  const [userRatings, setUserRatings] = useState(null);
  const [loadingRatings, setLoadingRatings] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      // Handle both old array response and new paginated response
      const data = response.data;
      setUsers(Array.isArray(data) ? data : data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlock = async (userId, isBlocked) => {
    try {
      if (isBlocked) {
        await axios.post(`${API}/admin/unblock-user/${userId}`);
        toast.success("Usuario desbloqueado");
      } else {
        await axios.post(`${API}/admin/block-user/${userId}`);
        toast.success("Usuario bloqueado");
      }
      fetchUsers();
    } catch (error) {
      toast.error("Error al actualizar usuario");
    }
  };

  const viewRatings = async (user) => {
    setSelectedUser(user);
    setShowRatingsDialog(true);
    setLoadingRatings(true);
    
    try {
      const response = await axios.get(`${API}/admin/user-ratings/${user.id}`);
      setUserRatings(response.data);
    } catch (error) {
      console.error("Error fetching ratings:", error);
      setUserRatings({ ratings: [], average_rating: null });
    } finally {
      setLoadingRatings(false);
    }
  };

  return (
    <AdminLayout title="Usuarios">
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-stone-600">No hay usuarios registrados</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rating privado</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Fecha de registro</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.private_rating ? (
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-400 fill-current" />
                        <span>{user.private_rating}</span>
                        <span className="text-stone-400 text-xs">({user.total_ratings || 0})</span>
                      </div>
                    ) : (
                      <span className="text-stone-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.blocked ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                        <AlertCircle className="w-3 h-3" />
                        Bloqueado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <CheckCircle className="w-3 h-3" />
                        Activo
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {format(parseISO(user.created_at), "d 'de' MMMM, yyyy", { locale: es })}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => viewRatings(user)}
                        title="Ver calificaciones"
                        data-testid={`view-ratings-${user.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className={user.blocked ? "text-green-600 hover:text-green-700 hover:bg-green-50" : "text-red-600 hover:text-red-700 hover:bg-red-50"}
                        onClick={() => handleBlock(user.id, user.blocked)}
                        title={user.blocked ? "Desbloquear" : "Bloquear"}
                        data-testid={`toggle-block-${user.id}`}
                      >
                        {user.blocked ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Ratings Dialog */}
      <Dialog open={showRatingsDialog} onOpenChange={setShowRatingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Calificaciones privadas</DialogTitle>
            <DialogDescription>
              Calificaciones dadas por listeners a {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          
          {loadingRatings ? (
            <div className="flex justify-center py-8">
              <div className="spinner"></div>
            </div>
          ) : userRatings?.ratings?.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-stone-50 rounded-lg">
                <Star className="w-5 h-5 text-amber-400 fill-current" />
                <span className="text-lg font-semibold">{userRatings.average_rating}</span>
                <span className="text-stone-500">promedio de {userRatings.total_ratings} calificaciones</span>
              </div>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {userRatings.ratings.map((rating) => (
                  <div key={rating.id} className="flex items-center gap-2 p-2 border border-stone-200 rounded">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${star <= rating.rating ? "text-amber-400 fill-current" : "text-stone-300"}`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-stone-500">
                      {format(parseISO(rating.created_at), "d/M/yyyy")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-center text-stone-500 py-8">
              Este usuario no tiene calificaciones aún
            </p>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
